import React, { memo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReactECharts  from 'echarts-for-react';

import { formatNumber_N1 } from '../../helpers/utils';

export default memo(function StockCandleStickIndayChart(props) {
    const { code } = props;
    const {t} = useTranslation();
    const refChart = useRef(null);
    
    //
    const c_counter = useSelector(state => state.gSymbol.symbolMatch[code]?.counter);
    const c_chartDatas = useSelector(state => state.gSymbol.symbolMatch[code]?.datas);

    //
    const upColor = '#54B770', downColor = '#E46161', textColor = '#D6D8DA';
    const _getChartOptions = () => {
        let _dataSet = [];
        if(c_chartDatas && c_chartDatas.length > 0) {
            let prevClosePrice = 0;
            c_chartDatas.sort((a, b) => a.time - b.time).forEach(item => {
                const date = new Date(item.time);
                const time =  date.getHours().toString().padStart(2, "0") + ":" + date.getMinutes().toString().padStart(2, "0");

                let _sign = 1;
                if(item.openPrice > item.closePrice) {
                    _sign = -1;
                }
                else if(item.openPrice < item.closePrice) {
                    _sign = 1;
                }
                else if(prevClosePrice < item.closePrice)  {
                    _sign = 1;
                }
                else if(prevClosePrice > item.closePrice)  {
                    _sign = -1;
                }

                // [open, close, lowest, highest]
                const pointData = [
                    time,
                    item.openPrice,
                    item.closePrice,
                    item.lowestPrice,
                    item.highestPrice,
                    item.volume, 
                    _sign
                ];
                //
                prevClosePrice = item.closePrice;
                _dataSet.push(pointData);
            });
        }

        //
        return {
            useUTC: false,
            textStyle: {
                fontFamily: "tahoma, 'Be Vietnam Pro'",
            },
            dataset: {
                source: _dataSet
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross'
                },
                borderWidth: 1,
                padding: 10,
                confine: true,
                formatter: function(params) {
                    // [time, open, close, lowest, highest, volume, sign]
                    // console.log(params);
                    if(params && params.length > 0) {
                        const data = params[0].data;
                        const marker = params[0].marker;
                        const subMarker = '<span style="display:inline-block;margin-right:4px;margin-bottom:3px;border-radius:5px;width:5px;height:5px;width:5px;background-color:' + params[0].color + ';"></span>';
                        //
                        let rez = '<p>' + data[0] + '</p>';
                        rez += '<div class="chart-tooltip-grid-3">';
                        rez += '<div style="grid-column: 1 / span 3; padding: 3px 0"></div>';
                        rez += '<div>' + marker + '</div><div>' + t('gia') + '</div><div class="text-right"></div>';
                        rez += '<div class="text-center">' + subMarker + '</div><div>' + t('mo') + ':</div><div class="text-right"><b>' + formatNumber_N1(data[1]) + '</b></div>';
                        rez += '<div class="text-center">' + subMarker + '</div><div>' + t('dong') + ':</div><div class="text-right"><b>' + formatNumber_N1(data[2]) + '</b></div>';
                        rez += '<div class="text-center">' + subMarker + '</div><div>' + t('thap') + ':</div><div class="text-right"><b>' + formatNumber_N1(data[3]) + '</b></div>';
                        rez += '<div class="text-center">' + subMarker + '</div><div>' + t('cao') + ':</div><div class="text-right"><b>' + formatNumber_N1(data[4]) + '</b></div>';
                        rez += '<div style="grid-column: 1 / span 3; padding: 3px 0"></div>';
                        rez += '<div>' + marker + '</div><div>' + t('tongKL') + ':</div><div class="text-right"><b>' + formatNumber_N1(data[5]) + '</b></div>';
                        rez += '</div>';
                        return rez;
                    }
                },
            },
            axisPointer: {
                link: [
                    {
                        xAxisIndex: 'all'
                    }
                ],
                label: {
                    backgroundColor: '#777'
                }
            },
            visualMap: [{
                show: false,
                seriesIndex: 0,
                dimension: 6,
                pieces: [
                    {
                        value: 1,
                        color: upColor,
                    },
                    {
                        value: -1,
                        color: downColor,
                    }
                ]
            }, {
                show: false,
                seriesIndex: 1,
                dimension: 6,
                pieces: [
                    {
                        value: 1,
                        color: upColor,
                    },
                    {
                        value: -1,
                        color: downColor,
                    }
                ]
            }],
            xAxis: [
                {
                    type: 'category',
                    demension: 0,
                    scale: true,
                    axisLabel: { color: textColor, },
                    axisLine: { onZero: false, lineStyle: { color: textColor }, },
                    axisTick: { lineStyle: { color: textColor } },
                    splitLine: { show: false },
                    min: 'dataMin',
                    max: 'dataMax',
                    axisPointer: {
                        z: 100
                    },
                },
                {
                    type: 'category',
                    demension: 0,
                    gridIndex: 1,
                    scale: true,
                    axisLabel: { show: false },
                    axisLine: { onZero: false, lineStyle: { color: textColor }, },
                    axisTick: { show: false },
                    splitLine: { show: false },
                    min: 'dataMin',
                    max: 'dataMax',
                }
            ],
            yAxis: [
                {
                    scale: true,
                    splitLine: { 
                        lineStyle: {
                            color: "#6E7079",
                            type: 'dashed',
                        }
                    },
                    axisLabel: { color: textColor, },
                    axisLine: { onZero: false, lineStyle: { color: textColor } },
                },
                {
                    scale: true,
                    gridIndex: 1,
                    splitNumber: 2,
                    axisLabel: { show: false },
                    axisLine: { show: false },
                    axisTick: { show: false },
                    splitLine: { show: false },
                    min: 0,
                }
            ],
            series: [
                {
                    name: 'Price',
                    type: 'candlestick',
                    itemStyle: {
                        color: upColor,
                        color0: downColor,
                        borderColor: upColor,
                        borderColor0: downColor,
                    },
                    encode: {
                        x: 0,
                        y: [1, 2, 3, 4],
                    },
                    barMaxWidth: 30,
                },
                {
                    name: 'Volume',
                    type: 'bar',
                    xAxisIndex: 1,
                    yAxisIndex: 1,
                    encode: {
                        x: 0,
                        y: 5,
                    },
                    barMaxWidth: 30,
                }
            ],
            grid: [{
                left: _dataSet && _dataSet.length > 0 ? '70px' : '20px',
                right: '20px',
                bottom: '30%',
            },
            {
                left: _dataSet && _dataSet.length > 0 ? '70px' : '20px',
                right: '20px',
                top: '80%',
                bottom: '20px',
            }],
        }
    }

    const _handleWindowResize = () => {
        if(refChart && refChart.current)
        {
            refChart.current.getEchartsInstance().resize();
        }
    }

    useEffect(() => {
        if(refChart && refChart.current)
        {
            refChart.current.getEchartsInstance().setOption(_getChartOptions());
        }
    });

    useEffect(() => {
        window.addEventListener('resize', _handleWindowResize);
        return () => {
            window.removeEventListener('resize', _handleWindowResize);
        };
    }, [])

    //
    return (
        <ReactECharts style={{width: '100%', height: '100%', ...props.style}}
            ref={refChart}
            lazyUpdate={true}
            option={_getChartOptions()}
        />
    )
}, (prevProps, nextProps) => {
    if (prevProps.code === nextProps.code) {
        return true; // props are equal
    }
    return false; // props are not equal -> update the component
})
